import React from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from '../types';

interface LegalFooterProps {
  company: Company | null;
  notes?: string;
  vatExemptArticle?: string; // Override: mention TVA auto-liquidation intracom (P0-08)
}

/**
 * Pied de page avec mentions légales
 * Affiche SIRET, TVA, RCS, etc.
 */
export const LegalFooter: React.FC<LegalFooterProps> = ({ company, notes, vatExemptArticle }) => {
  const { t } = useTranslation();
  if (!company) return null;

  const legalParts: string[] = [];

  // Ajouter les informations légales
  if ((company as any).registrationNumber) {
    legalParts.push(`SIRET: ${(company as any).registrationNumber}`);
  }
  if ((company as any).vatNumber) {
    legalParts.push(`TVA: ${(company as any).vatNumber}`);
  }
  if ((company as any).legalForm) {
    legalParts.push((company as any).legalForm);
  }
  if (company.capital) {
    legalParts.push(`Capital: ${company.capital}€`);
  }
  

  return (
    <div className="mt-8 pt-6 pb-8 print:pb-16 border-t border-gray-300 space-y-4">
      {/* Notes personnalisées */}
      {notes && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <h4 className="font-semibold text-blue-900 mb-2 text-sm">{t('templates.common.notes')}</h4>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{notes}</p>
        </div>
      )}

      {/* Conditions Générales de Vente */}
      {(company as any).legalNotesDefault && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-700 mb-2 text-sm">{t('templates.common.cgv')}</h4>
          <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
            {(company as any).legalNotesDefault}
          </div>
        </div>
      )}

      {/* Informations bancaires */}
      {(company.iban || company.bic) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-700 mb-2 text-sm">{t('templates.common.bankDetails')}</h4>
          <div className="text-xs text-gray-600 space-y-1">
            {company.iban && (
              <p>
                <span className="font-medium">IBAN:</span> {company.iban}
              </p>
            )}
            {company.bic && (
              <p>
                <span className="font-medium">BIC:</span> {company.bic}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Informations légales */}
      {legalParts.length > 0 && (
        <div className="text-xs text-gray-600 text-center space-y-1">
          <p>{legalParts.join(' • ')}</p>
        </div>
      )}

      {/* Mention TVA exemption ou auto-liquidation intracom (P0-08) */}
      {vatExemptArticle && (
        <div className="text-xs text-gray-500 italic text-center">
          <p>{t('templates.common.vatNotApplicable')} - {vatExemptArticle}</p>
        </div>
      )}
    </div>
  );
};
